import { bindEvents } from './helpers';

import AnimationController from './controller/animation';
import AudioController from './controller/audio';
import CursorController from './controller/cursor';
import OverlayController from './controller/overlay';
import PageController from './controller/page';
import RevealController from './controller/reveal';
import RoutingController from './controller/routing';
import ScrollController from './controller/scroll';
import SectionController from './controller/section';


class App {

	constructor() {
		this.el = null;

		this.touch = false;

		this.timeout = null;

		this.transition = false;
		this.coverVideoTunnel = null;
		this.muted = false;

		this.events = bindEvents(this);

		this.addEventListeners();
	}

	collectGlobalElements() {
		this.el = {
			fixedHeader: document.querySelector('.fixed-header'),
			smoothScrollContent: document.querySelector('.smooth-scroll-content'),
			cursorTakeover: document.querySelector('.cursor-takeover__cursor'),
			videoPlayer: document.querySelector('.video-player'),
			gallery: document.querySelector('.gallery'),
			nav: document.querySelector('.nav'),
			share: document.querySelector('.share'),
			splashScreen: document.querySelector('.splash-screen'),
			coverIntro: document.querySelector('.cover-intro'),
			masterWipe: document.querySelector('.master-wipe')
		};
	}

	configureController() {
		PageController.contentWrapper = this.el.smoothScrollContent;

		OverlayController.overlays = {
			videoPlayer: this.el.videoPlayer,
			gallery: this.el.gallery,
			nav: this.el.nav,
			share: this.el.share
		};

		ScrollController.scrollWrapper = this.el.smoothScrollContent;
		ScrollController.start();

		RevealController.selector = '[data-scroll-reveal]';
		RevealController.imageSelector = '[data-image-auto-swap]';
		RevealController.start();

		CursorController.cursor = this.el.cursorTakeover;
		CursorController.progressCircle = this.el.cursorTakeover.querySelector('.cursor-takeover__progress path');
		CursorController.selector = '[data-cursor-layer]';
		CursorController.defaultLayer = 'main';
		CursorController.collectAll();
		CursorController.start();

		AnimationController.start();

		AudioController.add('background', document.body.dataset.backgroundAudio);
		AudioController.audio.background.loop = true;

		SectionController.initializeGlobal({
			'modules/fixed-header': this.el.fixedHeader,
			'modules/video-player': this.el.videoPlayer,
			'modules/gallery': this.el.gallery,
			'modules/nav': this.el.nav,
			'modules/share': this.el.share,
			'modules/cover-intro': this.el.coverIntro
		});

		RoutingController.selector = 'a:not([href*="http"]):not([data-bypass])';
		RoutingController.overrideAnchor();
		RoutingController.start({
			'/': () => this.openChapter(0),
			'/:chapter': (chapter) => {
				const index = parseInt(chapter, 10);
				this.openChapter(index);
			}
		});
	}

	openChapter(chapter) {
		const { active } = PageController;
		if (chapter !== active) {
			this.firstVisit().then(() => {
				this.checkTransition().then(() => {
					this.renderChapter(chapter, active);
				});
			});

			gtag('event', 'screen_view', {
				'app_name': 'moves',
				'screen_name': `chapter-0${chapter}`
			});
			gtag('config', 'UA-20805242-4', {
				'page_title': `chapter-0${chapter}`,
				'page_path': `/0${chapter}`
			});
		} else {
			this.reset();
		}
	}

	firstVisit() {
		return new Promise((resolve) => {
			if (sessionStorage.getItem('first-visit')) {
				SectionController.globals['fixed-header'].show();
				resolve();
			} else {
				SectionController.globals['fixed-header'].hide();
				this.el.splashScreen.classList.add('is-visible');
				CursorController.startHijack('dark-dot');
				setTimeout(() => {
					sessionStorage.setItem('first-visit', Date.now());
					CursorController.startHijack('press-hold');
					this.el.splashScreen.classList.remove('is-visible');
					document.documentElement.classList.add('showing-intro');
					SectionController.globals['cover-intro'].callback = resolve;
					SectionController.globals['cover-intro'].el.video.play();
				}, 4000);
			}
		});
	}

	checkTransition() {
		return new Promise((resolve) => {
			if (this.timeout) clearTimeout(this.timeout);
			if (this.transition) {
				this.el.masterWipe.classList.add('is-wiping');
				this.timeout = setTimeout(() => {
					this.timeout = setTimeout(() => this.el.masterWipe.classList.remove('is-wiping'), 1000);
					resolve();
				}, 1000);
			} else {
				this.transition = true;
				resolve();
			}
		});
	}

	renderChapter(chapter, active) {
		PageController.open(chapter).then((page) => {
			this.reset();
			this.startBackgroundAudio();

			if (active !== null) {
				SectionController.destroy(active);
				PageController.close(active);
			}

			SectionController.initialize(chapter, page);
			ScrollController.scrollContent = page;
			RevealController.collectAll(page);
			CursorController.refreshLayer('main');
			RoutingController.overrideAnchor(page);

			const images = page.querySelectorAll('img');
			for (const image of images) {
				image.addEventListener('load', () => {
					ScrollController.measureContentHeight();
					CursorController.refreshLayer('main');
				});
			}
		});
	}

	reset() {
		OverlayController.close();
		ScrollController.scrollTo(0, true);
	}

	sendCoverVideo(video) {
		this.coverVideoTunnel = video.parentNode.removeChild(video);
	}

	receiveCoverVideo() {
		if (this.coverVideoTunnel !== null) {
			const video = this.coverVideoTunnel;
			this.coverVideoTunnel = null;
			return video;
		}
	}

	startBackgroundAudio() {
		const audio = AudioController.audio.background;
		if (!this.muted && audio.paused) {
			audio.play()
				.then(() => AudioController.volume('background', 1))
				.catch(() => {
					// console.log(e);
				});
		}
	}

	_onDOMContentLoaded() {
		this.collectGlobalElements();
		this.configureController();
	}

	_onDocumentTouchStart() {
		this.touch = true;
		CursorController.remove();
		ScrollController.remove();
		document.removeEventListener('touchstart', this.events._onDocumentTouchStart);
	}

	addEventListeners() {
		document.addEventListener('DOMContentLoaded', this.events._onDOMContentLoaded);
		document.addEventListener('touchstart', this.events._onDocumentTouchStart, { passive: true });
	}

}

export default new App();
